import { MoreHorizontal, MessageSquare, ExternalLink, Link as LinkIcon, Check, Video, History } from 'lucide-react';
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { AGENT_WORKING_IDLE_MS, agentWorkingNow, type AgentWorking, type TicketSummary, type User, type Column } from '@vd/types';
import { Badge } from '../primitives/Badge';
import { Tooltip } from '../primitives/Tooltip';
import { AssigneeStack } from './AssigneeStack';
import { JiraChip } from './JiraChip';
import { RollingCount } from './RollingCount';
import { cn } from '../cn';
import { displayTicketTitle, isTitlePending } from '../lib/displayTicketTitle';
import { sourceUrlLabel } from '../lib/sourceUrlLabel';
import styles from './KCard.module.css';

export interface KCardProps {
  ticket: TicketSummary;
  creator?: User;
  assignees?: User[];
  column?: Column;
  /** Thumbnail image URL (when thumbnails are enabled). */
  thumbnailUrl?: string;
  /** Thumbnails toggle state. When false, card is a dense row. */
  showThumbnail?: boolean;
  /** Pre-rendered thumbnail node (used for static mocks). */
  thumbnailSlot?: ReactNode;
  selected?: boolean;

  /** When true, a checkbox is rendered at the top-left of the card (over
   * the thumbnail, or in the card body when dense). Used by the bulk-action
   * flow on the board / cards / inbox views — NOT the single view. */
  bulkSelectable?: boolean;
  /** Is this card currently part of the bulk selection? */
  bulkSelected?: boolean;
  /** Called when the checkbox is clicked. Parent owns the selection Set. */
  onBulkToggle?: (ticketId: string, nextSelected: boolean) => void;

  /** Render a "moving / processing" state — dimmed card with a spinner.
   * Used while a bulk operation (move, archive, assign) is in flight so
   * the user gets immediate feedback that work is happening on this card,
   * even before the server confirms. Independent of bulkSelected. */
  isMoving?: boolean;

  onOpen?: (ticket: TicketSummary) => void;
  onCopyLink?: (ticket: TicketSummary) => void;
  onOpenSource?: (ticket: TicketSummary) => void;
  onStatusClick?: (ticket: TicketSummary, anchor: HTMLElement) => void;
  /** Makes the type pill a dropdown, as the status pill is. Without it the pill only shows the type. */
  onTypeClick?: (ticket: TicketSummary, anchor: HTMLElement) => void;
  onAssignClick?: (ticket: TicketSummary, anchor: HTMLElement) => void;
  onOverflowClick?: (ticket: TicketSummary, anchor: HTMLElement) => void;
  /**
   * Opens the ticket's Session window over the page (TOADO-945). The button
   * shows only with this and only on a ticket with something to show (hasSession).
   */
  onSessionClick?: (ticket: TicketSummary, anchor: HTMLElement) => void;

  /**
   * The card's time, as the app shows times (TOADO-904): in the person's own
   * zone, with the full date on hover. No fallback to the browser's format.
   */
  formatTime: (iso: string) => ReactNode;
  className?: string;
}

function stop(e: MouseEvent) { e.stopPropagation(); }

/**
 * The agent at work on this ticket, if one is (t-gdi2Cd), and a re-render
 * when it goes quiet: the board does not refetch for the clock, so the card
 * schedules its own look again for the moment the quiet limit passes.
 */
function useAgentWorking(ticket: TicketSummary): AgentWorking | null {
  const [now, setNow] = useState(() => Date.now());
  const agent = agentWorkingNow(ticket, now);
  const until = agent ? Date.parse(agent.lastActiveAt) + AGENT_WORKING_IDLE_MS : null;
  useEffect(() => {
    if (until === null) return;
    const timer = window.setTimeout(() => setNow(Date.now()), Math.max(0, until - Date.now()) + 250);
    return () => window.clearTimeout(timer);
  }, [until]);
  return agent;
}

/**
 * True for `ms` after `condition` turns true while the card is on screen, so a
 * change the person can watch plays once: never on the first render, which is
 * how the card already was.
 */
function useJustBecame(condition: boolean, ms: number): boolean {
  const previous = useRef(condition);
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (condition && !previous.current) setOn(true);
    previous.current = condition;
  }, [condition]);
  useEffect(() => {
    if (!on) return;
    const timer = window.setTimeout(() => setOn(false), ms);
    return () => window.clearTimeout(timer);
  }, [on, ms]);
  return on;
}

/** How recent a ticket's creation is for its card to glide in as it appears. */
const ARRIVAL_WINDOW_MS = 20_000;

/** The title, word by word, each a beat after the one before (at most 0.7s in all). */
function ArrivingTitle({ title }: { title: string }) {
  const parts = title.split(/(\s+)/);
  let word = 0;
  return (
    <>
      {parts.map((part, i) => /^\s+$/.test(part) || part === ''
        ? part
        : <span key={i} className={styles.titleWord} style={{ animationDelay: `${Math.min(word++ * 45, 700)}ms` }}>{part}</span>)}
    </>
  );
}

export function KCard({
  ticket, creator, assignees = [], column, thumbnailUrl, showThumbnail = true, thumbnailSlot, selected,
  bulkSelectable, bulkSelected, onBulkToggle, isMoving,
  onOpen, onCopyLink, onOpenSource, onStatusClick, onTypeClick, onAssignClick, onOverflowClick, onSessionClick,
  formatTime, className,
}: KCardProps) {
  const handleOpen = () => onOpen?.(ticket);
  const time = formatTime(ticket.createdAt);
  const title = displayTicketTitle(ticket);
  const isPending = isTitlePending(ticket);
  const agent = useAgentWorking(ticket);
  // Small moments, each played once as it happens (Jason, 18 Oct): the AI's
  // title writing itself in, a new ticket gliding onto the board, and the
  // resolved checks drawing themselves. None under reduced motion (the CSS).
  const titleArrived = useJustBecame(!isPending, 1600);
  const justResolved = useJustBecame(!!ticket.resolved, 1200);
  const [arriving] = useState(() => {
    const age = Date.now() - Date.parse(ticket.createdAt);
    return age >= 0 && age < ARRIVAL_WINDOW_MS;
  });

  // Render the thumbnail area only when the caller asked for it AND there's
  // something to put in it. A ticket with no thumbnail (e.g., text-only or
  // pre-thumbnail-generation capture) collapses to dense mode for its card
  // specifically — an empty white rectangle looks broken.
  const hasThumbContent = !!thumbnailSlot || !!thumbnailUrl;
  const renderThumb = showThumbnail && hasThumbContent;

  // The bulk-select checkbox. Stops propagation so the card's open-ticket
  // click handler doesn't fire when toggling the box. `inline`: in the bottom
  // row of a card without a thumbnail, rather than over the thumbnail.
  const bulkCheck = (inline: boolean) => (
    <label
      className={cn(styles.bulkCheckWrap, inline && styles.bulkCheckInline)}
      onClick={(e) => e.stopPropagation()}
      aria-label={bulkSelected ? 'Deselect ticket' : 'Select ticket'}
    >
      <input
        type="checkbox"
        checked={!!bulkSelected}
        onChange={(e) => onBulkToggle?.(ticket.id, e.target.checked)}
        className={styles.bulkCheck}
      />
      <span className={styles.bulkCheckMark} aria-hidden>
        <Check size={12} strokeWidth={3} />
      </span>
    </label>
  );

  // Open, Copy, Session where there is one (TOADO-945) and More. On a card with a picture they sit over its bottom
  // left (Jason, 18 Oct), which frees the footer's row; a card without one
  // keeps them in its footer. Shown on rollover either way.
  const actions = (onImage: boolean) => (
    <div className={cn(styles.actions, onImage && styles.actionsOnImage)} onClick={stop}>
      <Tooltip content={`Open ${sourceUrlLabel(ticket.sourceUrl)}`} multiline>
        <button className={styles.actionBtn} onClick={() => onOpenSource?.(ticket)} aria-label="Open source">
          <ExternalLink size={12} /> <span className={styles.actionLabel}>Open</span>
        </button>
      </Tooltip>
      <Tooltip content="Copy ticket link">
        <button className={styles.actionBtn} onClick={() => onCopyLink?.(ticket)} aria-label="Copy link">
          <LinkIcon size={12} /> <span className={styles.actionLabel}>Copy</span>
        </button>
      </Tooltip>
      {onSessionClick && ticket.hasSession && (
        <Tooltip content="What they did before reporting">
          <button
            className={styles.actionBtn}
            onClick={(e) => onSessionClick(ticket, e.currentTarget)}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label="Session"
            data-testid="card-session"
          >
            <History size={12} /> <span className={styles.actionLabel}>Session</span>
          </button>
        </Tooltip>
      )}
      <button
        className={cn(styles.actionBtn, styles.overflow)}
        onClick={(e) => onOverflowClick?.(ticket, e.currentTarget)}
        aria-label="More"
      >
        <MoreHorizontal size={14} />
      </button>
    </div>
  );

  // A video, comments, AI activity, resolved: beside the time on every card (Jason, 18 and 22 Oct).
  // Only the video is marked: every capture has a screenshot.
  const meta = (ticket.hasVideo || ticket.commentCount > 0 || ticket.hasAiActivity || ticket.resolved) && (
    <div className={styles.meta}>
      {ticket.hasVideo && (
        <Tooltip content="Has a video">
          <span className={styles.chip} data-testid="card-video" aria-label="Has a video"><Video size={11} aria-hidden="true" /></span>
        </Tooltip>
      )}
      {ticket.commentCount > 0 && (
        <span className={styles.chip}><MessageSquare size={11} /><RollingCount value={ticket.commentCount} /></span>
      )}
      {ticket.hasAiActivity && <Tooltip content="AI activity on this ticket"><span className={styles.aiDot} /></Tooltip>}
      {ticket.resolved && (
        <Tooltip content="Resolved">
          <span className={cn(styles.chip, styles.resolved, justResolved && styles.resolvedDrawing)}>
            <Check size={11} /><Check size={11} />
          </span>
        </Tooltip>
      )}
    </div>
  );

  return (
    <article
      data-testid="board-card"
      data-ticket-id={ticket.id}
      // What the board tour opens (TOADO-988).
      data-tour-anchor={ticket.isSample ? 'board.sample-ticket' : undefined}
      className={cn(
        styles.card,
        selected && styles.selected,
        bulkSelected && styles.bulkSelected,
        !renderThumb && styles.dense,
        isMoving && styles.moving,
        agent && styles.agentWorking,
        arriving && styles.arriving,
        className,
      )}
      data-agent-working={agent ? '' : undefined}
      onClick={handleOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        // ARIA role="button" must activate on Enter AND Space. Previously
        // only Enter was handled, so keyboard users couldn't open cards
        // with Space (the native <button> default).
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleOpen();
        }
      }}
      aria-label={title}
      aria-busy={isMoving || undefined}
    >
      {isMoving && (
        // Dim overlay + centered spinner. Pointer-events:none on the
        // overlay so card clicks (or keyboard activation) still pass
        // through to the article — the parent decides whether the card
        // is interactive during a bulk operation; we don't lock it from
        // the leaf component.
        <div className={styles.movingOverlay} aria-hidden="true">
          <span className={styles.movingSpinner} />
        </div>
      )}
      {/* Over the top-left of the thumbnail. A card without one has it in its
          bottom row instead, beside Open (below). */}
      {bulkSelectable && renderThumb && bulkCheck(false)}
      {renderThumb && (
        <div className={styles.thumb}>
          {thumbnailSlot ?? (
            <img
              src={thumbnailUrl}
              alt=""
              loading="lazy"
              // Hide the element on load failure so a 404 or tainted data:
              // URL (e.g., from a cleared-but-not-invalidated localStorage
              // entry) doesn't render the browser's broken-image icon.
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          {ticket.health !== 'complete' && (
            <Tooltip content={ticket.health === 'partial' ? 'Capture partial' : 'Capture failed'}>
              <span className={cn(styles.health, ticket.health === 'failed' && styles.healthFailed)}>
                <span className={styles.healthDot} /> {ticket.health === 'partial' ? 'Partial' : 'Failed'}
              </span>
            </Tooltip>
          )}
          {actions(true)}
        </div>
      )}

      <div className={styles.body}>
        <div className={styles.peopleRow}>
          <AssigneeStack
            creator={creator}
            assignees={assignees}
            size="sm"
            onClickAdd={(e) => onAssignClick?.(ticket, e.currentTarget)}
          />
          <div className={styles.pills}>
            <TypePill ticket={ticket} onTypeClick={onTypeClick} />
            {column && (
              <Tooltip content="Change column">
                <button
                  className={cn(styles.statusPill, column.tone && styles[`tone-${column.tone}`])}
                  onClick={(e) => { stop(e); onStatusClick?.(ticket, e.currentTarget); }}
                >
                  <span className={styles.statusDot} />
                  <span className={styles.pillLabel}>{column.name}</span>
                </button>
              </Tooltip>
            )}
          </div>
        </div>

        <div className={cn(styles.title, isPending && styles.titlePending)}>
          {titleArrived ? <ArrivingTitle title={title} /> : title}
        </div>
        {ticket.isSample && <div className={styles.sampleLine} data-testid="card-sample">Sample ticket: delete it whenever you like</div>}

        {/* Words as well as the shimmer, so the meaning survives a screenshot,
            a colour-blind viewer and reduced motion (t-gdi2Cd). */}
        {agent && (
          <div className={styles.agentLine} data-testid="card-agent-working">
            <span className={styles.agentPulse} aria-hidden />
            <span className={styles.agentText}>{agent.client} is working on this</span>
          </div>
        )}

        {/* The time, and where the ticket stands with Jira (TOADO-708): this
            line has the card's whole width, where the footer keeps room for
            its hover actions. */}
        <div className={styles.timeRow} data-testid="card-time-line">
          <div className={styles.timeStart}>
            <div className={styles.time} data-testid="card-time">{time}</div>
            {meta}
          </div>
          <JiraChip jira={ticket.jira} />
        </div>

        {!renderThumb && (
          <div className={styles.footer}>
            <div className={styles.footerStart}>
            {/* A card without a thumbnail keeps its checkbox here, left of Open,
                so it shows with the other rollover controls and covers nothing
                (TOADO-594); the rest of the card keeps its place. */}
            {bulkSelectable && bulkCheck(true)}
            {actions(false)}
            </div>
          </div>
        )}
      </div>

      {!renderThumb && ticket.health !== 'complete' && (
        <Badge variant={ticket.health === 'failed' ? 'danger' : 'warning'} dot className={styles.denseHealth}>
          {ticket.health === 'partial' ? 'Partial' : 'Failed'}
        </Badge>
      )}
    </article>
  );
}

/**
 * What kind of thing this is (TOADO-411), beside the column and styled as its
 * pill, so neither spends the title's width or the footer's.
 *
 * With `onTypeClick` it is a dropdown like the column's. An untyped ticket then
 * shows a quiet "Type" pill only while the card is hovered or focused, since
 * most of a workspace starts untyped and a pill on every card saying so is
 * noise. Without a handler it only shows a type that is set.
 */
function TypePill({ ticket, onTypeClick }: Pick<KCardProps, 'ticket' | 'onTypeClick'>) {
  const type = ticket.type;
  // A project can rename a workspace type; the tooltip says which one it is.
  const renamed = type?.companyName ? `${type.name} (${type.companyName} in this workspace)` : undefined;
  if (!onTypeClick) {
    if (!type) return null;
    return (
      <Tooltip content={renamed ?? type.name}>
        <span className={cn(styles.statusPill, styles.pillStatic)} data-ticket-type={type.glyph}>
          <span className={styles.pillLabel}>{type.name}</span>
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip content={type ? (renamed ? `Change type: ${renamed}` : 'Change type') : 'Set type'}>
      <button
        className={cn(styles.statusPill, !type && styles.typePillEmpty)}
        data-ticket-type={type?.glyph}
        aria-label={type ? `Type: ${type.name}. Change type` : 'Set type'}
        onClick={(e) => { stop(e); onTypeClick(ticket, e.currentTarget); }}
      >
        <span className={styles.pillLabel}>{type ? type.name : 'Type'}</span>
      </button>
    </Tooltip>
  );
}
